import type { KeyboardEventHandler, PropsWithChildren, Ref } from 'react';
import React, { useId } from 'react';
import { HBox, VBox } from '../../layout';
import { Badge, Icon, IconToolTip } from '../../basic';
import { intl, intlFmt } from '../../../lib';
import { mergeRefs } from '../../../util';
import type {
  ChatUser,
  MessageReaction,
  MessageStatus,
  ReactionClickHandler,
  UserState
} from '../chatTypes';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';
import { ChatPersonImage } from './ChatPersonImage';
import { formatChatMessageDate } from './chatDateFormatting';
import { ChatMessageReactions } from './ChatMessageReactions';
import { useMessageReactionPicker } from './useMessageReactionPicker';
import { EmojiReactionsButton } from '../EmojiReactionsButton';

/** Display name and screen-reader label of the sender. */
export function useChatMessagePerson(
  person: Omit<ChatUser, 'uid'>
): { displayName: string; messageFromLabel: string } {
  const { firstname, lastname } = person;
  const displayName = person.displayName ?? `${firstname} ${lastname}`.trim();
  const messageFromLabel = intlFmt('common.messageFrom', displayName);
  return { displayName, messageFromLabel };
}

/** Props shared by every concrete message component, e.g. {@link ChatTextMessage} and {@link ChatFileMessage}. */
export type CommonMessageProps = {
  /**
   * Person sending the message.
   */
  person: Omit<ChatUser, 'uid'>;
  /**
   * Date when message was sent. You can specify string for your date formatting, otherwise default will be used which renders time for today and date for older messages.
   */
  date: Date | string;
  /**
   * Text of the error.
   */
  errorText?: string;
  /**
   * Status of the message.
   */
  status?: MessageStatus;
  /**
   * Tab order for keyboard navigation within the chat message list.
   */
  tabIndex?: number;
  /**
   * Ref to the focusable message container.
   */
  messageRef?: Ref<HTMLDivElement>;
  /**
   * Keyboard handler for navigating between messages.
   */
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  /**
   * Focus handler to keep roving tabindex state in sync.
   */
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  /**
   * Called when ESC is pressed while the message itself is focused. Should
   * move the focus out of the message list (usually to the chat input) and
   * return `true` when it did so - the key event is then consumed.
   */
  onEscape?: () => boolean;
  emojiData?: EmojiData;
  reactions?: Array<MessageReaction>;
  reactionClick: ReactionClickHandler;
  eventId?: string;
  roomId?: string;
  userState?: UserState;
  reactionsDisabled?: boolean;
};

export type ChatMessageHeaderProps = {
  /** Class prefix, e.g. `text` or `file`. */
  prefix: string;
  displayName: string;
  isSender: boolean;
  /**
   * Status of the message.
   */
  status?: MessageStatus;
  formattedDate: string;
};

/** Header: sender name, status icon, time. */
export function ChatMessageHeader({
  prefix,
  displayName,
  isSender,
  status,
  formattedDate
}: Readonly<ChatMessageHeaderProps>): React.JSX.Element {
  return (
    <HBox className={`${prefix}-message-header`} wrap="wrap">
      {!isSender && (
        <>
          <div aria-hidden="true" className="username">
            {displayName}
          </div>
          <Icon
            iconName="dot"
            aria-label={undefined}
            className={`${prefix}-message-dot-icon gray`}
          />
        </>
      )}
      {isSender && status === 'read' && (
        <IconToolTip
          tooltip={intl('common.chatmessage.read')}
          iconName="eye-modern"
          className="message-status"
          role="status"
        />
      )}
      {isSender && status === 'sent' && (
        <IconToolTip
          tooltip={intl('common.chatmessage.sent')}
          iconName="shui_check"
          className="message-status"
          role="status"
        />
      )}
      {isSender && status === 'sending' && (
        <IconToolTip
          tooltip={intl('common.chatmessage.sending')}
          iconName="more-options"
          className="message-status"
          role="status"
        />
      )}
      <div className="gray">
        <time
          dateTime={formattedDate}
          aria-label={intlFmt('common.chatmessage.sentOn', formattedDate)}
        >
          {formattedDate}
        </time>
      </div>
    </HBox>
  );
}

export type ChatMessageFrameProps = PropsWithChildren<
  CommonMessageProps & {
    /** Class prefix, e.g. `text` or `file`. */
    prefix: string;
    /**
     * Disables the reaction picker, e.g. while a file upload is still
     * pending and no event id is available yet.
     */
    reactionsDisabled?: boolean;
  }
>;

/**
 * Shared layout and behaviour for all chat messages: avatar, header, error,
 * reaction picker and reaction pills. Body is `children`.
 */
export function ChatMessageFrame({
  prefix,
  person,
  date,
  errorText,
  status,
  userState,
  tabIndex,
  messageRef,
  onKeyDown,
  onFocus,
  onEscape,
  emojiData = { categories: {}, emojis: {} },
  reactions,
  reactionClick,
  eventId,
  roomId,
  reactionsDisabled = false,
  children
}: Readonly<ChatMessageFrameProps>): React.JSX.Element {
  const { firstname, lastname, isSender } = person;
  const { displayName, messageFromLabel } = useChatMessagePerson(person);
  const formattedDate = formatChatMessageDate(date);
  const userStatusId = useId();
  const errorId = useId();

  const ariaDescribedBy = [];
  if (userState && !isSender) {
    ariaDescribedBy.push(userStatusId);
  }
  if (status === 'error') {
    ariaDescribedBy.push(errorId);
  }

  const {
    isTouchDevice,
    mergedMessageRef,
    emojiButtonRef,
    reactionRefs,
    showReactionPicker,
    showReactionPickerTrigger,
    setShowReactionPickerTrigger,
    trapPickerFocus,
    toggleReactionPicker,
    openReactionPicker,
    closeReactionPicker,
    handlePickerReactionClick,
    handleFocus,
    handleBlur,
    handleKeyDown,
    layerProps,
    renderLayer,
    triggerRef,
    reactionDrawerOpen,
    setReactionDrawerOpen
  } = useMessageReactionPicker({
    isSender,
    reactionCount: reactions?.length ?? 0,
    reactionClick,
    messageRef,
    onKeyDown,
    onFocus,
    onEscape,
    disabled: reactionsDisabled
  });

  return (
    <HBox
      ref={mergeRefs<HTMLDivElement>([mergedMessageRef, triggerRef])}
      className={`ehs-chat-message chat-${prefix}-component ${
        isSender ? 'sender' : ''
      } ${isTouchDevice ? 'mobile' : ''}`}
      align={isSender ? 'end' : 'start'}
      aria-live="polite"
      role="group"
      tabIndex={tabIndex ?? -1}
      aria-describedby={ariaDescribedBy.join(' ') || undefined}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={() =>
        !isTouchDevice &&
        !reactionsDisabled &&
        setShowReactionPickerTrigger(true)
      }
      onMouseLeave={() => !isTouchDevice && setShowReactionPickerTrigger(false)}
      onClick={e => {
        if (!isTouchDevice || reactionsDisabled) return;
        if ((e.target as HTMLElement).closest('.EmojiPickerReact')) return;
        if (showReactionPicker && trapPickerFocus) {
          closeReactionPicker();
        } else {
          // The bar may already be open because the tap focused the message.
          // Turn it into the explicitly opened (focus trapping) picker
          // instead of closing it right away.
          openReactionPicker(true);
        }
      }}
    >
      <div className="sr-only">
        {!isSender ? messageFromLabel : intl('common.you')}
      </div>
      {!isSender && (
        <ChatPersonImage
          avatar="user"
          firstname={firstname}
          lastname={lastname}
          aria-hidden="true"
          userStatusId={userStatusId}
          userStatus={userState}
        />
      )}
      <VBox className={`${prefix}-message`}>
        <ChatMessageHeader
          prefix={prefix}
          displayName={displayName}
          isSender={isSender}
          status={status}
          formattedDate={formattedDate}
        />
        <HBox className={`${prefix}-message-container`}>
          {children}
          {!reactionsDisabled && (
            <EmojiReactionsButton
              emojiData={emojiData}
              reactionClick={handlePickerReactionClick}
              eventId={eventId}
              roomId={roomId}
              showReactionPicker={showReactionPicker}
              setShowReactionPicker={toggleReactionPicker}
              showReactionPickerTrigger={showReactionPickerTrigger}
              setShowReactionPickerTrigger={setShowReactionPickerTrigger}
              layerProps={layerProps}
              renderLayer={renderLayer}
              showTrigger={!isTouchDevice && !reactionsDisabled}
              trapFocus={trapPickerFocus}
              triggerRef={emojiButtonRef}
            />
          )}
        </HBox>
        <ChatMessageReactions
          reactions={reactions}
          eventId={eventId}
          roomId={roomId}
          emojiData={emojiData}
          reactionClick={reactionClick}
          isTouchDevice={isTouchDevice}
          reactionRefs={reactionRefs}
          reactionDrawerOpen={reactionDrawerOpen}
          setReactionDrawerOpen={setReactionDrawerOpen}
          reactionsDisabled={reactionsDisabled}
        />
        {status === 'error' && (
          <HBox className="chat-message-error" role="alert" id={errorId}>
            <Badge badgeType="error" />
            <p>
              {errorText ? errorText : intl('common.chatmessage.defaultError')}
            </p>
          </HBox>
        )}
      </VBox>
      {isTouchDevice && !reactionsDisabled && (
        <span className="sr-only">
          {intl('matrixChatPortlet.message.react.mobile')}
        </span>
      )}
    </HBox>
  );
}
